import { useAuth } from '@/contexts/auth';
import type { SessionI } from '@/types/cyclone/requests';

/**
 * Permisos de la sección Clientes para el profesional (venue_staff). Permisos v2 (14/10).
 *
 * El owner (no-staff) siempre tiene acceso total. Para el staff, los permisos que el owner
 * configura desde Equipo son INDEPENDIENTES entre sí:
 *  - canViewClients    → clients_view — entrar a Clientes, la ficha y las notas privadas.
 *                        `clients_view_all` ya NO la abre.
 *  - canViewContact    → clients_view_all — ver el contacto (email/teléfono) en la ficha, la
 *                        lista y el detalle del turno.
 *  - canEditClients    → clients_edit && clients_view — crear / editar la ficha.
 *  - canViewClientName → clients_name_view — nombre y apellido en la agenda, las reservas, la
 *                        ficha y el detalle del turno. Si falta en la sesión (payload viejo) se
 *                        trata como prendido: nunca escondemos nombres por un dato ausente.
 */
export interface ClientPermissions {
  canViewClients: boolean;
  canViewContact: boolean;
  canEditClients: boolean;
  canViewClientName: boolean;
}

export const clientPermissionsFor = (session: SessionI | null | undefined): ClientPermissions => {
  const perms = session?.vendor?.vendor_permissions;
  const isStaff = !!session?.is_staff;

  if (!isStaff) {
    return { canViewClients: true, canViewContact: true, canEditClients: true, canViewClientName: true };
  }

  return {
    canViewClients: !!perms?.clients_view,
    canViewContact: !!perms?.clients_view_all,
    canEditClients: !!(perms?.clients_edit && perms?.clients_view),
    canViewClientName: perms?.clients_name_view !== false
  };
};

/** Profesional del equipo sin «Puede ver el nombre de sus clientes»: la API manda los nombres en
 *  `null` y la UI muestra «Nombre oculto». */
export const isClientNameHidden = (session: SessionI | null | undefined): boolean =>
  !clientPermissionsFor(session).canViewClientName;

export const useClientPermissions = (): ClientPermissions => {
  const { session } = useAuth();
  return clientPermissionsFor(session);
};
